import React, { useMemo, useState } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import '../../adventure/shared/parchmentConfirm.css'
import MissionIssuerPicker from './MissionIssuerPicker'
import './MissionBoard.css'
import './MissionReactivateDialog.css'

function cleanupConfirmDom() {
  document.body.classList.remove('react-confirm-alert-body-element')
  document.body.style.overflow = ''
  document.querySelectorAll('.react-confirm-alert-overlay').forEach((el) => {
    try {
      el.remove()
    } catch {
      /* ignore */
    }
  })
}

function issuerDisplayName(mission) {
  return (
    mission?.issuer?.name ||
    mission?.currentIssuerName ||
    mission?.issuerLabel ||
    mission?.issuer?.label ||
    null
  )
}

/**
 * Diálogo de reactivación: mismo emisor o emisor distinto (picker).
 *
 * @param {object} opts
 * @param {object} opts.mission
 * @param {string} opts.territorySlug
 * @param {boolean} [opts.canSetCustomIssuer]
 * @param {boolean} [opts.canChooseDaimyo]
 * @param {boolean} [opts.hasDaimyo]
 * @param {string|null} [opts.hanName]
 * @param {object|null} [opts.activeCharacterAsIssuer]
 * @param {(payload: object) => void|Promise<void>} opts.onConfirm
 */
export function openMissionReactivateDialog({
  mission,
  territorySlug,
  canSetCustomIssuer = false,
  canChooseDaimyo = false,
  hasDaimyo = false,
  hanName = null,
  activeCharacterAsIssuer = null,
  onConfirm,
}) {
  const previousName = issuerDisplayName(mission)

  confirmAlert({
    closeOnEscape: true,
    closeOnClickOutside: false,
    customUI: ({ onClose }) => {
      const finish = () => {
        try {
          onClose()
        } catch {
          /* ignore */
        }
        cleanupConfirmDom()
      }

      function Body() {
        const [busy, setBusy] = useState(false)
        const [error, setError] = useState(null)
        const [sameIssuer, setSameIssuer] = useState(true)
        const [issuerMode, setIssuerMode] = useState('character')
        const [issuerCharacter, setIssuerCharacter] = useState(activeCharacterAsIssuer)
        const [issuerLabel, setIssuerLabel] = useState('')

        const modeHelp = useMemo(() => {
          if (sameIssuer) {
            return previousName
              ? `Se republicará con el mismo emisor («${previousName}»).`
              : 'Se republicará conservando el emisor actual.'
          }
          return 'Elige el emisor del nuevo aviso en el tablón.'
        }, [sameIssuer])

        const applyMode = (mode) => {
          setIssuerMode(mode)
          if (mode === 'character') {
            setIssuerLabel('')
            setIssuerCharacter((c) => c || activeCharacterAsIssuer)
          } else if (mode === 'daimyo') {
            setIssuerLabel('')
            setIssuerCharacter(null)
          } else if (mode === 'npc') {
            setIssuerCharacter(null)
          }
        }

        const submit = async () => {
          setError(null)
          const payload = { sameIssuer: Boolean(sameIssuer) }
          if (!sameIssuer) {
            const mode = canSetCustomIssuer ? issuerMode : 'character'
            if (mode === 'character') {
              const cid =
                issuerCharacter?.characterId || activeCharacterAsIssuer?.characterId || null
              if (!cid) {
                setError('Selecciona un personaje emisor')
                return
              }
              payload.issuerAsDaimyo = false
              payload.issuerLabel = null
              payload.issuerCharacterId = cid
              payload.characterId = cid
            } else if (mode === 'daimyo') {
              if (!hasDaimyo) {
                setError('Este Han no tiene Daimyo asignado')
                return
              }
              payload.issuerAsDaimyo = true
              payload.issuerLabel = null
              payload.issuerCharacterId = null
            } else if (mode === 'npc') {
              const label = String(issuerLabel || '').trim()
              if (!label) {
                setError('Indica el nombre del NPC emisor')
                return
              }
              payload.issuerAsDaimyo = false
              payload.issuerLabel = label.slice(0, 120)
              payload.issuerCharacterId = null
              payload.characterId = activeCharacterAsIssuer?.characterId || null
            }
          }

          setBusy(true)
          try {
            await onConfirm?.(payload)
            finish()
          } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'No se pudo reactivar')
            setBusy(false)
          }
        }

        return (
          <div
            className="parchment-confirm parchment-confirm--wide mission-reactivate-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mission-reactivate-title"
          >
            <div className="parchment-confirm__seal" aria-hidden="true">
              記
            </div>
            <h2 id="mission-reactivate-title" className="parchment-confirm__title">
              Reactivar misión
            </h2>
            <p className="parchment-confirm__message">
              Se creará un nuevo aviso activo (reintento). La misión actual permanece cerrada.
            </p>

            <div className="mission-reactivate-dialog__block">
              <span className="field-label">Emisor</span>
              {previousName ? (
                <p className="mission-reactivate-dialog__current">
                  Actual: <strong>{previousName}</strong>
                </p>
              ) : null}
              <div
                className="mission-reactivate-dialog__choices"
                role="radiogroup"
                aria-label="Conservar o cambiar emisor"
              >
                <label
                  className={`mission-reactivate-dialog__choice${
                    sameIssuer ? ' mission-reactivate-dialog__choice--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="mission-reactivate-same-issuer"
                    checked={sameIssuer}
                    disabled={busy}
                    onChange={() => setSameIssuer(true)}
                  />
                  Mismo emisor
                </label>
                <label
                  className={`mission-reactivate-dialog__choice${
                    !sameIssuer ? ' mission-reactivate-dialog__choice--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="mission-reactivate-same-issuer"
                    checked={!sameIssuer}
                    disabled={busy}
                    onChange={() => setSameIssuer(false)}
                  />
                  Emisor diferente
                </label>
              </div>
              <p className="mission-reactivate-dialog__hint">{modeHelp}</p>
            </div>

            {!sameIssuer && (
              <div className="mission-reactivate-dialog__picker">
                {canSetCustomIssuer ? (
                  <MissionIssuerPicker
                    territorySlug={territorySlug}
                    issuerMode={issuerMode}
                    issuerCharacter={issuerCharacter}
                    issuerLabel={issuerLabel}
                    canChooseDaimyo={canChooseDaimyo}
                    hasDaimyo={hasDaimyo}
                    hanName={hanName}
                    radioName="mission-reactivate-issuer-mode"
                    disabled={busy}
                    onModeChange={applyMode}
                    onCharacterChange={setIssuerCharacter}
                    onLabelChange={setIssuerLabel}
                  />
                ) : (
                  <p className="mission-reactivate-dialog__hint">
                    El emisor será{' '}
                    <strong>
                      {activeCharacterAsIssuer?.name || 'tu personaje activo'}
                    </strong>
                    .
                  </p>
                )}
              </div>
            )}

            {error ? <p className="mission-reactivate-dialog__error">{error}</p> : null}

            <div className="parchment-confirm__actions">
              <button
                type="button"
                className="parchment-confirm__btn parchment-confirm__btn--cancel"
                disabled={busy}
                onClick={finish}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="parchment-confirm__btn parchment-confirm__btn--confirm"
                disabled={busy}
                onClick={submit}
              >
                {busy ? 'Reactivando…' : 'Reactivar'}
              </button>
            </div>
          </div>
        )
      }

      return <Body />
    },
  })
}
